/**
 * Post Create Listener
 *
 * Registers event listeners used by the post create form workflow.
 */

import { getEventScope } from "../../core/event-scope/event-scope";
import { PostSharedTabsSetup } from "../tabs/post-tabs-setup";

type HandlerLike = Function | EventListener | EventListenerObject | null | undefined;

interface PostCreateListenerHandler {
    imageUploadHandler?: HandlerLike;
    videoUploadHandler?: HandlerLike;
    previewHandler?: HandlerLike;
    translationToggleHandler?: HandlerLike;
    ratingChangeHandler?: HandlerLike;
    titleChangeHandler?: HandlerLike;
    contentChangeHandler?: HandlerLike;
    starClickHandler?: HandlerLike;
    starHoverHandler?: HandlerLike;
    ratingMouseLeaveHandler?: HandlerLike;
    starKeydownHandler?: HandlerLike;
    updatePreviewContent: () => void;
}

interface PostCreateListenerElements {
    form?: HTMLFormElement | null;
    imageUpload?: HTMLInputElement | null;
    videoUpload?: HTMLInputElement | null;
    addImageBtn?: HTMLElement | null;
    addVideoBtn?: HTMLElement | null;
    previewBtn?: HTMLElement | null;
    translationToggle?: HTMLElement | null;
    ratingSlider?: HTMLElement | null;
    titleInput?: HTMLElement | null;
    contentTextarea?: HTMLElement | null;
}

class PostCreateListener {
    public static bindClick(element: HTMLElement, handler: (event: Event) => void): void {
        element.addEventListener("click", handler);
    }

    public static bindChange(
        element: HTMLElement,
        handler: (event: Event) => void,
        options?: AddEventListenerOptions,
    ): void {
        element.addEventListener("change", handler, options);
    }

    public static bindInput(
        element: HTMLElement,
        handler: (event: Event) => void,
        options?: AddEventListenerOptions,
    ): void {
        element.addEventListener("input", handler, options);
    }

    public static bindSubmit(
        form: HTMLFormElement,
        handler: (event: SubmitEvent) => void,
        options?: AddEventListenerOptions,
    ): void {
        form.addEventListener("submit", handler as EventListener, options);
    }

    public static bindShowMoreToggle(element: HTMLElement, handler: (event: Event) => void): void {
        element.addEventListener("show-more:toggle", handler);
    }

    public static bindTransitionEnd(element: HTMLElement, handler: (event: TransitionEvent) => void): void {
        element.addEventListener("transitionend", handler as EventListener);
    }

    public static unbindTransitionEnd(element: HTMLElement, handler: (event: TransitionEvent) => void): void {
        element.removeEventListener("transitionend", handler as EventListener);
    }

    public static bindWindowResize(handler: (event: UIEvent) => void): void {
        window.addEventListener("resize", handler as EventListener);
    }

    public static setup(handler: PostCreateListenerHandler, elements: PostCreateListenerElements): void {
        const imageUploadHandler = PostCreateListener.asEventListener(handler.imageUploadHandler);
        const videoUploadHandler = PostCreateListener.asEventListener(handler.videoUploadHandler);
        const previewHandler = PostCreateListener.asEventListener(handler.previewHandler);
        const translationToggleHandler = PostCreateListener.asEventListener(handler.translationToggleHandler);
        const ratingChangeHandler = PostCreateListener.asEventListener(handler.ratingChangeHandler);
        const titleChangeHandler = PostCreateListener.asEventListener(handler.titleChangeHandler);
        const contentChangeHandler = PostCreateListener.asEventListener(handler.contentChangeHandler);
        const starClickHandler = PostCreateListener.asEventListener(handler.starClickHandler);
        const starHoverHandler = PostCreateListener.asEventListener(handler.starHoverHandler);
        const ratingMouseLeaveHandler = PostCreateListener.asEventListener(handler.ratingMouseLeaveHandler);
        const starKeydownHandler = PostCreateListener.asEventListener(handler.starKeydownHandler);

        if (elements.imageUpload && imageUploadHandler) {
            elements.imageUpload.addEventListener("change", imageUploadHandler);
        }

        if (elements.addImageBtn && elements.imageUpload) {
            elements.addImageBtn.addEventListener("click", () => {
                elements.imageUpload?.click();
            });
        }

        if (elements.videoUpload && videoUploadHandler) {
            elements.videoUpload.addEventListener("change", videoUploadHandler);
        }

        if (elements.addVideoBtn && elements.videoUpload) {
            elements.addVideoBtn.addEventListener("click", () => {
                elements.videoUpload?.click();
            });
        }

        if (elements.previewBtn && previewHandler) {
            elements.previewBtn.addEventListener("click", previewHandler);
        }

        if (elements.translationToggle && translationToggleHandler) {
            elements.translationToggle.addEventListener("change", translationToggleHandler);
        }

        if (elements.ratingSlider && ratingChangeHandler) {
            elements.ratingSlider.addEventListener("input", ratingChangeHandler);
        }

        if (elements.titleInput && titleChangeHandler) {
            elements.titleInput.addEventListener("input", titleChangeHandler);
        }

        if (elements.contentTextarea && contentChangeHandler) {
            elements.contentTextarea.addEventListener("input", contentChangeHandler);
        }

        PostCreateListener.setupDragAndDrop(elements);
        PostCreateListener.setupTabSwitching(handler, elements.form || document);

        const scope = getEventScope(document, "post-create");
        scope.once("rating-delegation", () => {
            if (starClickHandler) {
                scope.on("click", starClickHandler);
            }

            if (starHoverHandler) {
                scope.on("mouseenter", starHoverHandler, { capture: true });
            }

            if (ratingMouseLeaveHandler) {
                scope.on("mouseleave", ratingMouseLeaveHandler, { capture: true });
            }

            if (starKeydownHandler) {
                scope.on("keydown", starKeydownHandler, { capture: true });
            }
        });
    }

    public static setupDragAndDrop(elements: PostCreateListenerElements): void {
        if (!(elements.form instanceof HTMLFormElement)) return;

        ["dragover", "drop"].forEach((eventName) => {
            elements.form?.addEventListener(eventName, (event) => {
                if (event instanceof DragEvent && event.dataTransfer?.files?.length) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            });
        });
    }

    public static setupTabSwitching(
        handler: Pick<PostCreateListenerHandler, "updatePreviewContent">,
        rootEl: Document | HTMLElement = document,
    ): void {
        PostSharedTabsSetup.setupPostTabSwitching({
            rootEl,
            onPreview: () => handler.updatePreviewContent(),
        });
    }

    public static cleanup(handler: PostCreateListenerHandler, elements: PostCreateListenerElements): void {
        const imageUploadHandler = PostCreateListener.asEventListener(handler.imageUploadHandler);
        const videoUploadHandler = PostCreateListener.asEventListener(handler.videoUploadHandler);
        const previewHandler = PostCreateListener.asEventListener(handler.previewHandler);
        const translationToggleHandler = PostCreateListener.asEventListener(handler.translationToggleHandler);
        const ratingChangeHandler = PostCreateListener.asEventListener(handler.ratingChangeHandler);
        const titleChangeHandler = PostCreateListener.asEventListener(handler.titleChangeHandler);
        const contentChangeHandler = PostCreateListener.asEventListener(handler.contentChangeHandler);
        const starClickHandler = PostCreateListener.asEventListener(handler.starClickHandler);
        const starHoverHandler = PostCreateListener.asEventListener(handler.starHoverHandler);
        const ratingMouseLeaveHandler = PostCreateListener.asEventListener(handler.ratingMouseLeaveHandler);
        const starKeydownHandler = PostCreateListener.asEventListener(handler.starKeydownHandler);

        if (elements.imageUpload && imageUploadHandler) {
            elements.imageUpload.removeEventListener("change", imageUploadHandler);
        }

        if (elements.videoUpload && videoUploadHandler) {
            elements.videoUpload.removeEventListener("change", videoUploadHandler);
        }

        if (elements.previewBtn && previewHandler) {
            elements.previewBtn.removeEventListener("click", previewHandler);
        }

        if (elements.translationToggle && translationToggleHandler) {
            elements.translationToggle.removeEventListener("change", translationToggleHandler);
        }

        if (elements.ratingSlider && ratingChangeHandler) {
            elements.ratingSlider.removeEventListener("input", ratingChangeHandler);
        }

        if (elements.titleInput && titleChangeHandler) {
            elements.titleInput.removeEventListener("input", titleChangeHandler);
        }

        if (elements.contentTextarea && contentChangeHandler) {
            elements.contentTextarea.removeEventListener("input", contentChangeHandler);
        }

        if (starClickHandler) {
            document.removeEventListener("click", starClickHandler);
        }
        if (starHoverHandler) {
            document.removeEventListener("mouseenter", starHoverHandler, true);
        }
        if (ratingMouseLeaveHandler) {
            document.removeEventListener("mouseleave", ratingMouseLeaveHandler, true);
        }
        if (starKeydownHandler) {
            document.removeEventListener("keydown", starKeydownHandler, true);
        }

        PostSharedTabsSetup.cleanupPostTabSwitching(document);
    }

    private static asEventListener(handler: HandlerLike): EventListener | null {
        if (typeof handler === "function") {
            return handler as EventListener;
        }

        if (handler && typeof handler === "object" && "handleEvent" in handler) {
            return handler.handleEvent.bind(handler);
        }

        return null;
    }
}

export { PostCreateListener };
